// Libraries
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { identifyPosthogUser, resetPosthog } from "config/posthog-runtime";
import { isInternalAnalyticsUser } from "lib/analytics/internal-user";
import { clearStoredReferralCode, getStoredReferralCode } from "utils/referralStorage";
import {
  buildAuthMutationHeaders,
  splitTurnstileHeaders,
  type WithTurnstileHeaders,
} from "utils/authTurnstileRequest";

// Store
import { setRegisterInfo } from "store/reducers/register";
import {
  AuthState,
  logout as logoutAuth,
  removeMfaSessionId,
  removeRememberMe,
  removeSsoLogin,
  setEmail,
  setLogin,
  setMfaSessionId,
  setRefreshToken,
  setRememberMe,
  setSsoLogin,
  setToken,
} from "store/reducers/auth";
import { logout as logoutProject } from "store/reducers/project";
import { logout as logoutProfile } from "store/reducers/profile";
import {
  LoginDataType,
  MfaSetupType,
  RegisterDataType,
  SsoLoginDataType,
  UserInfoDataType,
  UserMfaLoginType,
} from "store/types/auth";
import { status, SubscriptionType } from "store/types/enum";
import { AppState } from "../reducers/Reducer";

// Hooks
import { useAnalytics } from "hooks/useAnalytics";
import { useAuthAttemptMutation } from "hooks/useAuthAttemptMutation";
import { ANALYTICS_EVENTS } from "analytics";

// Utils
import { toast } from "react-toastify";
import { handleToast } from "utils/toasts";
import { getToastMessageForError, handleAxiosRequest, ServerError } from "utils";
import { getDeviceHeaders } from "utils/deviceMetadata";
import { buildAuthErrorProperties, isEmailTakenError } from "utils/authErrorAnalytics";
import { oauthRedirectStorage, resolvePostLoginPath } from "utils/redirectUtils";
import { buildSignupAttributionHeaders } from "utils/signupAttribution";
import {
  ApiErrorCode,
  ENVS,
  isPosthogEnabled,
  PATH_AUTH,
  PATH_DASHBOARD,
  toastMsg,
  USER_API,
} from "config";
import { ResendCancelDeletionToast } from "pages/auth/cancelDeletion/components/ResendCancelDeletionToast";
import { invitationStorage, extractInvitationTokenFromUrl } from "utils/invitationUtils";
import { catalogKeys, sessionKeys } from "queries/profile.keys";
import { authClient, signOut as betterAuthSignOut } from "lib/auth-client";
import {
  clearBearerSessionToken,
  getBearerSessionToken,
  setBearerSessionToken,
} from "lib/better-auth-session";
import { toSessionUser } from "hooks/useSession/toSessionUser";
import { fetchUserCatalog } from "hooks/useSession/fetchUserCatalog";
import {
  completeBetterAuthMfaLogin,
  signInWithBetterAuthEmail,
  signUpWithBetterAuthEmail,
} from "lib/better-auth-login";
import { signInWithBetterAuthSocial } from "lib/better-auth-social";
import { isNoUsableSsoProvider } from "lib/better-auth-sso";
import { resendBetterAuthVerificationEmail } from "lib/better-auth-email-verification";
import { setBetterAuthTwoFactorRedirectHandler } from "lib/better-auth-two-factor-bridge";
import { grantProductAnalyticsConsent } from "lib/analytics/product-analytics-consent";
import { measureOpenAiAdsRegistrationCompleted } from "lib/analytics/openai-ads-pixel";
import { useStepUpProtectedAction } from "hooks/auth";
import {
  assertBetterAuthLoginCurrent,
  tagBetterAuthLoginResult,
} from "lib/complete-better-auth-login";
import {
  AuthSessionChangedError,
  captureAuthSession,
  isAuthSessionCurrent,
} from "lib/auth-session-ownership";
import { disposeAllRecordSaves } from "utils/recordSaveQueue";
import {
  assertAuthAttemptCurrent,
  authAttemptFetchOptions,
  bindAuthAttemptResult,
} from "lib/auth-attempt";

export const useAuth = (onMfaRequired?: () => void) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { runProtectedAction } = useStepUpProtectedAction();

  const token = useSelector((state: AppState) => state.auth.token);
  const refresh_token = useSelector((state: AppState) => state.auth.refresh_token);
  const email = useSelector((state: AppState) => state.auth.email);
  const userId = useSelector((state: AppState) => state.auth.user_id);
  const mfaSessionId = useSelector((state: AppState) => state.auth.mfa_session_id);
  const rememberMe = useSelector((state: AppState) => state.auth.remember_me);

  useEffect(() => {
    if (!ENVS.betterAuthEnabled) {
      return;
    }
    setBetterAuthTwoFactorRedirectHandler(onMfaRequired ?? null);
    return () => setBetterAuthTwoFactorRedirectHandler(null);
  }, [onMfaRequired]);

  const saveEmail = useCallback(
    (email: string) => {
      dispatch(setEmail(email));
    },
    [dispatch]
  );

  const saveToken = useCallback(
    (token: string) => {
      dispatch(setToken(token));
      if (token) {
        setBearerSessionToken(token);
      } else {
        clearBearerSessionToken();
      }
    },
    [dispatch]
  );

  const saveRefreshToken = useCallback(
    (newRefreshToken: string) => {
      dispatch(setRefreshToken(newRefreshToken));
    },
    [dispatch]
  );

  const saveRegisterInfo = (registerData: RegisterDataType) => {
    dispatch(setRegisterInfo(registerData));
  };

  const saveLoginData = (res: UserInfoDataType) => {
    assertBetterAuthLoginCurrent(res);
    const expectedSession = {
      generation: captureAuthSession().generation + 1,
      token: res.data.access_token,
      refreshToken: res.data.refresh_token,
      userId: res.data.user.user_id,
    };
    toast.dismiss();
    grantProductAnalyticsConsent();
    dispatch(setLogin(res));
    bindAuthAttemptResult(res, expectedSession);

    const sessionToken = res.data.access_token?.trim();
    if (sessionToken) {
      setBearerSessionToken(sessionToken);
    }

    queryClient.setQueryData(sessionKeys.me(), toSessionUser(res.data.user));
    void queryClient
      .prefetchQuery({
        queryKey: catalogKeys.me(),
        queryFn: () =>
          fetchUserCatalog(
            axios.create({
              headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : undefined,
            })
          ),
      })
      .catch(() => undefined);

    if (isPosthogEnabled) {
      const isInternalUser = isInternalAnalyticsUser(res.data.user.email);
      identifyPosthogUser(res.data.user.user_id, {
        email: res.data.user.email,
        first_name: res.data.user.first_name,
        last_name: res.data.user.last_name,
        is_mfa_enabled: res.data.user.mfa_enabled,
        is_verified: res.data.user.verified,
        is_active: res.data.user.is_active,
        subscription_pricing_plan: res.data.user.pricing_plan?.plan_name || SubscriptionType.FREE,
        created_at: res.data.user.created_at,
        is_internal_user: isInternalUser,
      });
    }

    // Guard in case UserGuiding hasn't been injected yet
    window.userGuiding?.identify?.(res.data.user.user_id, {
      email: res.data.user.email,
    });
  };

  const updateMfaSessionId = (res?: UserMfaLoginType) => {
    if (res) {
      dispatch(setMfaSessionId(res.data.session_id));
    } else {
      dispatch(removeMfaSessionId());
    }
  };

  const updateSsoLogin = (res?: AuthState["sso_login"]) => {
    if (res) {
      dispatch(setSsoLogin(res));
    } else {
      dispatch(removeSsoLogin());
    }
  };

  const handleLogOut = useCallback(async () => {
    const owner = captureAuthSession();
    const headers: Record<string, string> = { ...getDeviceHeaders() };
    const sessionToken = owner.token.trim() || getBearerSessionToken();
    if (sessionToken) {
      headers.Authorization = `Bearer ${sessionToken}`;
    }

    // Revoke the current session on the server while the token is still valid.
    await axios.post(USER_API.logout, {}, { withCredentials: true, headers }).catch(() => {
      // Logout API call failed - continue silently
    });

    if (!isAuthSessionCurrent(owner)) return;
    if (ENVS.betterAuthEnabled) {
      await betterAuthSignOut({
        fetchOptions: {
          headers: getDeviceHeaders(),
          auth: { type: "Bearer", token: sessionToken ?? "" },
          onRequest: () => {
            if (!isAuthSessionCurrent(owner)) throw new AuthSessionChangedError();
          },
        },
      }).catch(() => {
        // Better Auth sign-out is best-effort cookie/header cleanup.
      });
    }

    if (!isAuthSessionCurrent(owner)) return;
    clearBearerSessionToken();

    // Queued budget writes must not go out with the next user's sign-in.
    disposeAllRecordSaves();

    try {
      queryClient.cancelQueries();
      queryClient.clear();
    } catch (err) {
      // noop: cache clearing is best-effort
    }

    try {
      invitationStorage.clearPendingInvitation();
      localStorage.removeItem("user-preferences");
      localStorage.removeItem("draft-data");
      sessionStorage.clear();
    } catch (err) {
      // noop: storage clearing is best-effort
    }

    dispatch(logoutAuth());
    dispatch(logoutProject());
    dispatch(logoutProfile());
    navigate(PATH_AUTH.login, { replace: true });
    if (isPosthogEnabled) {
      resetPosthog();
    }
  }, [dispatch, navigate, queryClient]);

  const signUpMutation = useAuthAttemptMutation(
    async (signUpData: WithTurnstileHeaders<RegisterDataType>, attempt) => {
      const { payload, turnstileHeaders } = splitTurnstileHeaders(signUpData);

      if (ENVS.betterAuthEnabled) {
        const headers = buildAuthMutationHeaders(turnstileHeaders, {
          ...buildSignupAttributionHeaders(),
          ...(payload.referral_code
            ? { "x-referral-code": payload.referral_code.trim().toLowerCase() }
            : {}),
        });
        return signUpWithBetterAuthEmail(payload, headers, attempt);
      }

      return handleAxiosRequest<UserInfoDataType>(
        axios.post(USER_API.register, payload, {
          headers: buildAuthMutationHeaders(turnstileHeaders),
          withCredentials: true,
        })
      );
    },
    {
      onSuccess: (res, signUpData) => {
        assertBetterAuthLoginCurrent(res);
        const generation = captureAuthSession().generation + 1;
        const {
          data: { access_token, user },
        } = res;
        grantProductAnalyticsConsent();
        clearStoredReferralCode();
        // Clear any previous session to avoid stale persisted auth/profile causing redirects
        dispatch(logoutAuth());

        // Save minimal auth needed to resend verification email without logging in fully
        saveToken(access_token);
        saveRefreshToken(access_token);
        bindAuthAttemptResult(res, {
          generation,
          token: access_token,
          refreshToken: access_token,
          userId: "",
        });
        saveEmail(user.email);
        saveRegisterInfo(signUpData);
        // Store signup timestamp for rate limiting
        localStorage.setItem(`signupTime_${user.email}`, Date.now().toString());

        const signUpAnalyticsProperties = {
          userId: user.user_id,
          email: user.email,
          first_name: user.first_name || undefined,
          last_name: user.last_name || undefined,
          sign_up_method: "email" as const,
          created_at: user.created_at,
        };

        if (isPosthogEnabled) {
          const isInternalUser = isInternalAnalyticsUser(user.email);
          identifyPosthogUser(user.user_id, {
            email: user.email,
            first_name: user.first_name || undefined,
            last_name: user.last_name || undefined,
            created_at: user.created_at,
            is_internal_user: isInternalUser,
          });
        }

        trackEvent(ANALYTICS_EVENTS.SIGN_UP, signUpAnalyticsProperties);
        trackEvent(ANALYTICS_EVENTS.SIGN_UP_COMPLETED, signUpAnalyticsProperties);
        measureOpenAiAdsRegistrationCompleted();

        // Check for invitation token in redirect URL and store it
        const urlParams = new URLSearchParams(window.location.search);
        const redirectUrl = urlParams.get("redirect");

        if (redirectUrl) {
          const invitationToken = extractInvitationTokenFromUrl(redirectUrl);
          if (invitationToken) {
            // Store invitation token for later use after email verification and login
            invitationStorage.setPendingInvitation(invitationToken, user.email);
          }
        }

        if (ENVS.onPrem) {
          // On-prem deployments do not require email verification; user should proceed to login
          navigate(PATH_AUTH.login);
        } else if (user.verified) {
          // User is already verified: a deep link carried through sign-up (?redirect=) wins
          // over the dashboard.
          navigate(resolvePostLoginPath(window.location.search, PATH_DASHBOARD.dashboard));
        } else {
          // User needs email verification
          navigate(PATH_AUTH.resendEmail);
        }
      },
      onError: (error: ServerError | any) => {
        trackEvent(ANALYTICS_EVENTS.SIGN_UP_ERROR, {
          sign_up_method: "email",
          ...buildAuthErrorProperties("signup", error),
        });
        if (!(error instanceof ServerError)) {
          handleToast(toastMsg.genericServerErr, status.ERROR);
          return;
        }
        // The sign-up page shows this one inline with Log in and Reset password links.
        if (isEmailTakenError(error)) {
          return;
        }
        handleToast(getToastMessageForError(error), status.ERROR);
      },
    }
  );

  const loginMutation = useAuthAttemptMutation(
    async (
      loginData: WithTurnstileHeaders<LoginDataType>,
      attempt
    ): Promise<UserInfoDataType | UserMfaLoginType> => {
      const { payload, turnstileHeaders } = splitTurnstileHeaders(loginData);

      if (ENVS.betterAuthEnabled) {
        const result = await signInWithBetterAuthEmail(payload, turnstileHeaders, attempt);
        assertAuthAttemptCurrent(attempt);
        if (result.twoFactorRequired) {
          saveEmail(payload.email);
          dispatch(setRememberMe(payload.remember_me ?? true));
          onMfaRequired?.();
          return tagBetterAuthLoginResult(
            {
              status_code: 200,
              message: "MFA required",
              data: { session_id: "better-auth-mfa" },
            },
            attempt.session
          );
        }
        if (!result.userInfo) {
          throw new ServerError(500, "Sign in failed");
        }
        return result.userInfo;
      }

      return handleAxiosRequest<UserInfoDataType>(
        axios.post(USER_API.login, payload, {
          headers: buildAuthMutationHeaders(turnstileHeaders),
        })
      );
    },
    {
      onSuccess: (res: UserInfoDataType | UserMfaLoginType, loginData) => {
        assertBetterAuthLoginCurrent(res);
        if ("session_id" in res.data) {
          updateMfaSessionId(res as UserMfaLoginType);
          saveEmail(loginData.email);
          dispatch(setRememberMe(loginData.remember_me ?? true));
          onMfaRequired?.();
          return; // Exit early for MFA - don't process as regular login
        }
        res = res as UserInfoDataType;
        saveLoginData(res);

        trackEvent("sign_in", {
          userId: res.data.user.user_id,
          sign_up_method: "email",
          is_mfa_enabled: res.data.user.mfa_enabled,
          is_verified: res.data.user.verified,
          is_active: res.data.user.is_active,
          subscription_type: res.data.user.pricing_plan?.plan_name || SubscriptionType.FREE,
        });

        // If user has not verified their email (skip verification gating on on-prem)
        if (!res.data.user.verified && !ENVS.onPrem) {
          navigate(PATH_AUTH.resendEmail);
        } else {
          // Return to the page that sent them to login (?redirect=), else the dashboard.
          navigate(resolvePostLoginPath(window.location.search, PATH_DASHBOARD.dashboard));
        }
      },
      onError: (error: ServerError | any, loginData) => {
        trackEvent("sign_in_error", {
          sign_up_method: "email",
          ...buildAuthErrorProperties("password", error),
        });
        const statusCode = error?.status_code;
        const code = error?.code;
        const isLockout = statusCode === 401 && code === ApiErrorCode.TEMPORARY_LOCKOUT;
        if (isLockout) {
          const data = error?.data;
          const lockedUntil = data?.locked_until;
          const lockoutMessage =
            typeof lockedUntil === "string"
              ? (() => {
                  const date = new Date(lockedUntil);
                  if (Number.isNaN(date.getTime())) return toastMsg.logIn.error.temporaryLockout;
                  const timeStr = date.toLocaleTimeString(undefined, {
                    hour: "numeric",
                    minute: "2-digit",
                  });
                  return `Your account is temporarily locked. Try again after ${timeStr}.`;
                })()
              : toastMsg.logIn.error.temporaryLockout;
          handleToast(lockoutMessage, status.ERROR);
          return;
        }
        if (statusCode === 403 && code === ApiErrorCode.ACCOUNT_DEACTIVATED) {
          handleToast(getToastMessageForError(error), status.ERROR);
          return;
        }
        // Their organization now requires SSO. Send them where they can actually
        // sign in instead of leaving them to guess.
        if (code === ApiErrorCode.SSO_REQUIRED_FOR_DOMAIN) {
          handleToast(getToastMessageForError(error), status.ERROR);
          navigate(PATH_AUTH.ssoLogin, { state: { email: loginData.email } });
          return;
        }
        if (statusCode === 403 && code === ApiErrorCode.ACCOUNT_PENDING_DELETION) {
          const daysRemaining = (error?.data as { days_remaining?: number })?.days_remaining;
          const message =
            typeof daysRemaining === "number"
              ? daysRemaining === 0
                ? "Your account is scheduled for deletion today."
                : daysRemaining === 1
                  ? "Your account is scheduled for deletion in 1 day."
                  : `Your account is scheduled for deletion in ${daysRemaining} days.`
              : getToastMessageForError(error);
          toast(
            <ResendCancelDeletionToast
              message={message}
              email={loginData.email}
              onCancelDeletion={() => navigate(PATH_AUTH.cancelDeletion)}
            />,
            {
              position: "bottom-left",
              autoClose: 10_000,
              pauseOnHover: true,
              hideProgressBar: false,
              closeOnClick: false,
              closeButton: true,
              className: "!bg-transparent !shadow-none !p-0 !border-0",
            }
          );
          return;
        }
        if (!(error instanceof ServerError)) {
          handleToast(toastMsg.genericServerErr, status.ERROR);
          return;
        }
        if (error.status_code === 400 || error.status_code === 401) {
          handleToast(getToastMessageForError(error), status.ERROR);
          return;
        }
        if (error.status_code === 404) {
          handleToast(toastMsg.logIn.error.exist, status.ERROR);
        } else {
          handleToast(getToastMessageForError(error), status.ERROR);
        }
      },
    }
  );

  const mfaLoginMutation = useAuthAttemptMutation(
    async (loginData: WithTurnstileHeaders<MfaSetupType>, attempt) => {
      const { payload, turnstileHeaders } = splitTurnstileHeaders(loginData);

      if (ENVS.betterAuthEnabled) {
        return completeBetterAuthMfaLogin(
          { ...payload, email: payload.email?.trim() || email || undefined },
          attempt
        );
      }

      return handleAxiosRequest<UserInfoDataType>(
        axios.post(USER_API.mfaCompleteLogin, payload, {
          headers: buildAuthMutationHeaders(turnstileHeaders),
        })
      );
    },
    {
      onSuccess: (res) => {
        saveLoginData(res);
        updateMfaSessionId();
        dispatch(removeRememberMe());

        trackEvent("sign_in", {
          userId: res.data.user.user_id,
          sign_up_method: "email",
          is_mfa_enabled: res.data.user.mfa_enabled,
          is_verified: res.data.user.verified,
          is_active: res.data.user.is_active,
          subscription_type: res.data.user.pricing_plan?.plan_name || SubscriptionType.FREE,
        });
        navigate(resolvePostLoginPath(window.location.search, PATH_DASHBOARD.dashboard));
      },
      onError: (error: ServerError | any) => {
        trackEvent("sign_in_error", {
          sign_up_method: "email",
          ...buildAuthErrorProperties("totp", error),
        });
        if (!(error instanceof ServerError)) {
          handleToast(toastMsg.genericServerErr, status.ERROR);
          return;
        }
        throw error;
      },
    }
  );

  const ssoLoginMutation = useAuthAttemptMutation(
    async (params: SsoLoginDataType, attempt): Promise<void> => {
      saveEmail(params.email);
      updateSsoLogin();

      const callbackURL = `${window.location.origin}${PATH_AUTH.ssoCallback}`;
      const errorCallbackURL = `${window.location.origin}${PATH_AUTH.ssoCallback}?sso_error=1`;

      const result = await authClient.signIn.sso({
        email: params.email,
        callbackURL,
        errorCallbackURL,
        fetchOptions: authAttemptFetchOptions(attempt),
      });
      assertAuthAttemptCurrent(attempt);

      if (result.error) {
        const message =
          typeof result.error.message === "string" ? result.error.message : "SSO sign-in failed";
        const statusCode = isNoUsableSsoProvider(result.error.status) ? 404 : 400;
        throw new ServerError(statusCode, message);
      }

      const redirectUrl =
        result.data && typeof result.data === "object" && "url" in result.data
          ? (result.data as { url?: string }).url
          : undefined;

      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    },
    {
      onError: (error) => {
        if (!(error instanceof ServerError)) {
          handleToast(toastMsg.genericServerErr, status.ERROR);
          return;
        }
        if (error.status_code === 404) {
          handleToast(toastMsg.sso.error.exist, status.ERROR);
        } else {
          handleToast(getToastMessageForError(error), status.ERROR);
        }
      },
    }
  );

  const socialOAuthLoginMutation = useAuthAttemptMutation(
    async (provider: "google" | "linkedin", attempt) => {
      const ref = getStoredReferralCode();
      // The OAuth round trip drops our query string; keep the return path for the callback.
      oauthRedirectStorage.save(window.location.search);
      if (ENVS.betterAuthEnabled) {
        sessionStorage.setItem("oauthProvider", provider);
        // Sent on every social sign-in: only the backend knows whether it creates an account.
        await signInWithBetterAuthSocial(provider, ref, buildSignupAttributionHeaders(), attempt);
        return { data: { login_url: "", state: "", nonce: "" } };
      }

      return handleAxiosRequest<{
        data: { login_url: string; state: string; nonce: string };
      }>(axios.get(USER_API.initiateSocialOAuth(provider, ref), { withCredentials: true }));
    },
    {
      onSuccess: (res, provider) => {
        if (ENVS.betterAuthEnabled) {
          return;
        }

        const { login_url, nonce } = res.data;
        sessionStorage.setItem("oauthNonce", nonce);
        sessionStorage.setItem("oauthProvider", provider);
        window.location.href = login_url;
      },
      onError: (error) => {
        if (!(error instanceof ServerError)) {
          handleToast(toastMsg.genericServerErr, status.ERROR);
          return;
        }
        handleToast(getToastMessageForError({ code: error.code, message: error.message }), "error");
      },
    }
  );

  const disableAccountMutation = useMutation(
    () => handleAxiosRequest(axios.post(USER_API.disableAccount)),
    {
      onSuccess: handleLogOut,
      onError: (error) => {
        if (!(error instanceof ServerError)) {
          handleToast(toastMsg.genericServerErr, status.ERROR);
          return;
        }
        handleToast(getToastMessageForError(error), status.ERROR);
      },
    }
  );

  const resendEmailMutation = useMutation(
    async (vars?: { turnstileHeaders?: Record<string, string> }) => {
      if (ENVS.betterAuthEnabled) {
        return resendBetterAuthVerificationEmail(email, vars?.turnstileHeaders);
      }

      return handleAxiosRequest(
        axios.post(
          USER_API.resendEmail,
          { email },
          {
            headers: buildAuthMutationHeaders(vars?.turnstileHeaders, {
              Authorization: `Bearer ${token}`,
            }),
          }
        )
      );
    }
  );

  const activateAccountMutation = useMutation(
    () =>
      handleAxiosRequest(
        axios.post(USER_API.activateAccount, {}, { headers: { Authorization: `Bearer ${token}` } })
      ),
    {
      onSuccess(res: any) {
        handleToast(res.message, status.SUCCESS);
        // Force a fresh session so is_active / plan state is reloaded after activate.
        handleLogOut();
      },
      onError: (error) => {
        if (!(error instanceof ServerError)) {
          handleToast(toastMsg.genericServerErr, status.ERROR);
          return;
        }
        handleToast(getToastMessageForError(error), status.ERROR);
      },
    }
  );

  const deleteAccount = useMutation(
    (explanation: string) =>
      runProtectedAction(() =>
        handleAxiosRequest(
          axios.post(
            USER_API.deleteAccount,
            { explanation },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
        )
      ),
    {
      onSuccess: (res: any) => {
        handleToast(res?.message || "Account deletion scheduled.", status.SUCCESS);
        handleLogOut();
      },
      onError: (error: any) => {
        const msg =
          error?.response?.data?.message || error?.message || toastMsg.profile.error.unique;
        handleToast(msg, status.ERROR);
      },
    }
  );

  return {
    userId,
    token,
    refresh_token,
    email,
    mfaSessionId,
    rememberMe,
    saveToken,
    saveRefreshToken,
    signUpMutation,
    loginMutation,
    mfaLoginMutation,
    ssoLoginMutation,
    socialOAuthLoginMutation,
    disableAccountMutation,
    resendEmailMutation,
    activateAccountMutation,
    handleLogOut,
    saveLoginData,
    updateSsoLogin,
    deleteAccount,
  };
};
